import React from 'react';
import ReactMarkdown from 'react-markdown';
import { useApp } from '../../context/AppContext.tsx';
import {
  ShieldCheck,
  Compass,
  HeartHandshake,
  Award,
  Star,
  Clock,
  Copy,
  ArrowLeft,
  Edit3,
  FileQuestion,
  Layers,
} from 'lucide-react';

export default function CustomPageView() {
  const { settings, currentCustomPageSlug, copyPageUrl, navigate, user } = useApp();

  const customPages = settings?.customPages || [];
  const page = customPages.find(
    (p) => p.slug === currentCustomPageSlug || p.id === currentCustomPageSlug
  );

  const primaryColor = settings?.theme?.primaryColor || '#059669';

  const renderIcon = (iconName?: string) => {
    switch (iconName) {
      case 'Compass':
        return <Compass className="w-6 h-6" />;
      case 'HeartHandshake':
        return <HeartHandshake className="w-6 h-6" />;
      case 'Award':
        return <Award className="w-6 h-6" />;
      case 'ShieldCheck':
        return <ShieldCheck className="w-6 h-6" />;
      case 'Clock':
        return <Clock className="w-6 h-6" />;
      case 'Star':
      default:
        return <Star className="w-6 h-6" />;
    }
  };

  if (!page) {
    return (
      <div id="custom-page-not-found" className="py-20 px-4 text-center max-w-xl mx-auto">
        <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-200 text-amber-600 flex items-center justify-center mx-auto mb-4">
          <FileQuestion className="w-8 h-8" />
        </div>
        <h2 className="text-2xl font-black text-slate-900 mb-2">Halaman Tidak Ditemukan</h2>
        <p className="text-sm text-slate-500 mb-6">
          Halaman dengan URL <code className="px-2 py-0.5 bg-slate-100 rounded text-slate-800 font-mono">/page/{currentCustomPageSlug}</code> belum dibuat atau telah dinonaktifkan oleh administrator.
        </p>
        <button
          onClick={() => navigate('home')}
          className="px-6 py-2.5 rounded-xl text-white font-bold text-xs shadow-md transition-transform hover:scale-105"
          style={{ backgroundColor: primaryColor }}
        >
          Kembali ke Beranda
        </button>
      </div>
    );
  }

  return (
    <div id={`custom-page-${page.slug}`} className="py-10 sm:py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Navigation & Action Header */}
        <div className="flex items-center justify-between gap-4 mb-8">
          <button
            onClick={() => navigate('home')}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Kembali ke Beranda</span>
          </button>

          <div className="flex items-center gap-2">
            {user?.role === 'admin' && (
              <button
                onClick={() => {
                  try {
                    localStorage.setItem('bellva_admin_active_tab', 'pages');
                  } catch (e) {}
                  navigate('admin');
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 hover:bg-amber-100 text-amber-800 text-xs font-bold transition-all border border-amber-200"
              >
                <Edit3 className="w-3.5 h-3.5" />
                <span>Edit Halaman</span>
              </button>
            )}

            <button
              onClick={() => copyPageUrl(`/page/${page.slug}`, page.title)}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-all shadow-2xs border border-slate-200 cursor-pointer"
              title="Salin Tautan Halaman Ini"
            >
              <Copy className="w-3.5 h-3.5 text-slate-500" />
              <span>Salin URL (/page/{page.slug})</span>
            </button>
          </div>
        </div>

        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-10">
          {page.badge && (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs font-bold mb-3 border border-emerald-200">
              <Layers className="w-3.5 h-3.5" />
              <span>{page.badge}</span>
            </div>
          )}
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight">
            {page.title}
          </h1>
          {page.subtitle && (
            <p className="text-sm sm:text-base text-slate-500 mt-3 leading-relaxed">
              {page.subtitle}
            </p>
          )}
        </div>

        {/* Cover Image */}
        {page.coverImage && (
          <div className="rounded-3xl overflow-hidden shadow-xl aspect-16/9 mb-12 bg-slate-900 border border-slate-200">
            <img
              src={page.coverImage}
              alt={page.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Content Body */}
        <div className="bg-white p-6 sm:p-12 rounded-3xl border border-slate-200 shadow-xs mb-12">
          <div className="article-prose">
            <ReactMarkdown>{page.content || ''}</ReactMarkdown>
          </div>
        </div>

        {/* Key Values Grid */}
        {page.values && page.values.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
            {page.values.map((val) => (
              <div
                key={val.id}
                className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs transition-transform hover:-translate-y-1"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-white mx-auto mb-4 shadow-xs"
                  style={{ backgroundColor: primaryColor }}
                >
                  {renderIcon(val.icon)}
                </div>
                <h3 className="font-bold text-sm text-slate-900 mb-1.5">{val.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{val.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
